import { useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import type { ISensemakerListCard } from '@brushfire/types'
import { TruncatedTooltipText } from '@/shared/components/TruncatedTooltipText'
import { libraryCardHoverSx } from '@/shared/lib/libraryCardHover'
import {
  SENSEMAKER_LIST_CARD_BOTTOM_PADDING_PX,
  SENSEMAKER_LIST_CARD_HEADER_AVATAR_GAP_PX,
  SENSEMAKER_LIST_CARD_HEADER_AVATAR_SIZE_PX,
  SENSEMAKER_LIST_CARD_HEADER_BOTTOM_PADDING_PX,
  SENSEMAKER_LIST_CARD_HEADER_HEIGHT_PX,
  SENSEMAKER_LIST_CARD_HEADER_NAME_PACKAGE_GAP_PX,
  SENSEMAKER_LIST_CARD_HEADER_STATUS_RESERVE_PX,
  SENSEMAKER_LIST_CARD_HEADER_TO_STATS_GAP_PX,
  SENSEMAKER_LIST_CARD_HORIZONTAL_PADDING_PX,
  SENSEMAKER_LIST_CARD_INFO_HEIGHT_PX,
  SENSEMAKER_LIST_CARD_INFO_VALUE_LABEL_GAP_PX,
  SENSEMAKER_LIST_CARD_INFO_METRIC_GAP_PX,
  SENSEMAKER_LIST_CARD_INFO_TO_TOKEN_GAP_PX,
  SENSEMAKER_LIST_CARD_PROGRESS_TO_BUTTON_GAP_PX,
  SENSEMAKER_LIST_CARD_BUTTON_HEIGHT_PX,
  SENSEMAKER_LIST_CARD_TOKEN_LABEL_TO_PROGRESS_GAP_PX,
  SENSEMAKER_LIST_CARD_MIN_HEIGHT_PX,
  SENSEMAKER_LIST_CARD_TOP_PADDING_PX,
} from '../lib/sensemaker-list-layout'
import { SENSEMAKER_STATUS_COLORS, SENSEMAKER_STATUS_LABEL } from '../lib/sensemaker-status'
import {
  sensemakerCardDeactivateMenuItemSx,
  sensemakerCardMenuPaperSx,
  sensemakerCardReactivateMenuItemSx,
} from '../lib/sensemaker-card-menu'
import {
  SensemakerAmountIcon,
  SensemakerCardMoreIcon,
  SensemakerOnboardedIcon,
} from './SensemakerCardIcons'

function formatUsd(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount)
}

/** US-SML-1-12 — MM/DD/YY with leading zeros. */
function formatOnboardedDate(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const yy = String(d.getFullYear()).slice(-2)
  return `${month}/${day}/${yy}`
}

function tenantInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('')
}

interface Props {
  card: ISensemakerListCard
  onDeactivate: (card: ISensemakerListCard) => void
  onReactivate: (card: ISensemakerListCard) => void
}

interface InfoMetricProps {
  icon: ReactNode
  value: string
  valueColor: string
  label: string
}

function SensemakerCardInfoMetric({ icon, value, valueColor, label }: InfoMetricProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: `${SENSEMAKER_LIST_CARD_INFO_VALUE_LABEL_GAP_PX}px`,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
        }}
      >
        {icon}
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: '12px',
            lineHeight: '24px',
            color: valueColor,
            whiteSpace: 'nowrap',
          }}
        >
          {value}
        </Typography>
      </Box>
      <Typography
        sx={{
          fontSize: '9px',
          fontWeight: 400,
          lineHeight: '14px',
          color: '#04183f',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Typography>
    </Box>
  )
}

const STAT_TILES = [
  {
    key: 'campaigns',
    label: 'Active Campaigns',
    field: 'activeCampaignCount' as const,
    bg: '#e7fbff',
    border: '#c7ebf9',
    pl: '9px',
  },
  {
    key: 'instruments',
    label: 'Active Instruments',
    field: 'activeInstrumentCount' as const,
    bg: '#ffebee',
    border: '#ffd9ea',
    pl: '11px',
  },
  {
    key: 'team',
    label: 'Team Members',
    field: 'teamMemberCount' as const,
    bg: 'rgba(245,158,11,0.2)',
    border: '#fbe797',
    pl: '11px',
  },
]

export function SensemakerCard({ card, onDeactivate, onReactivate }: Props) {
  const navigate = useNavigate()
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const statusStyle = SENSEMAKER_STATUS_COLORS[card.status]

  const menuOpen = Boolean(menuAnchor)
  const showDeactivate = card.status === 'active'
  const showReactivate = card.status === 'deactivated'
  const showCardMenu = showDeactivate || showReactivate

  return (
    <Box
      data-testid={`sensemaker-card-${card.tenantId}`}
      sx={{
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
        bgcolor: '#ffffff',
        px: `${SENSEMAKER_LIST_CARD_HORIZONTAL_PADDING_PX}px`,
        pt: `${SENSEMAKER_LIST_CARD_TOP_PADDING_PX}px`,
        pb: `${SENSEMAKER_LIST_CARD_BOTTOM_PADDING_PX}px`,
        minHeight: `${SENSEMAKER_LIST_CARD_MIN_HEIGHT_PX}px`,
        display: 'flex',
        flexDirection: 'column',
        ...libraryCardHoverSx,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          minHeight: `${SENSEMAKER_LIST_CARD_HEADER_HEIGHT_PX}px`,
          pb: `${SENSEMAKER_LIST_CARD_HEADER_BOTTOM_PADDING_PX}px`,
          mb: `${SENSEMAKER_LIST_CARD_HEADER_TO_STATS_GAP_PX}px`,
          borderBottom: '1px solid #e2e8f0',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: SENSEMAKER_LIST_CARD_HEADER_AVATAR_SIZE_PX,
            height: SENSEMAKER_LIST_CARD_HEADER_AVATAR_SIZE_PX,
            borderRadius: '50%',
            bgcolor: '#008cd0',
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 600,
            fontSize: '12px',
            lineHeight: '22.5px',
          }}
        >
          {tenantInitials(card.tenantName)}
        </Box>
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '26px',
              px: '11px',
              borderRadius: '100px',
              border: `1px solid ${statusStyle.border}`,
              bgcolor: statusStyle.bg,
              color: statusStyle.color,
              fontSize: '12px',
              fontWeight: 500,
              lineHeight: '16px',
              whiteSpace: 'nowrap',
            }}
          >
            {SENSEMAKER_STATUS_LABEL[card.status]}
          </Box>
          <IconButton
            size="small"
            aria-label="Sensemaker actions"
            data-testid={`sensemaker-card-menu-${card.tenantId}`}
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            sx={{
              p: 0,
              width: 16,
              height: 16,
              color: '#0f172a',
              visibility: showCardMenu ? 'visible' : 'hidden',
              pointerEvents: showCardMenu ? 'auto' : 'none',
            }}
          >
            <SensemakerCardMoreIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
        <Box
          sx={{
            ml: `${SENSEMAKER_LIST_CARD_HEADER_AVATAR_SIZE_PX + SENSEMAKER_LIST_CARD_HEADER_AVATAR_GAP_PX}px`,
            pr: `${SENSEMAKER_LIST_CARD_HEADER_STATUS_RESERVE_PX}px`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: `${SENSEMAKER_LIST_CARD_HEADER_NAME_PACKAGE_GAP_PX}px`,
          }}
        >
          <Tooltip title={card.tenantName}>
            <Box sx={{ maxWidth: '100%' }}>
              <TruncatedTooltipText
                text={card.tenantName}
                typographySx={{
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: '22.5px',
                  color: '#0f172a',
                }}
              />
            </Box>
          </Tooltip>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: '8px',
              height: '19px',
              maxWidth: '100%',
              borderRadius: '10px',
              border: '0.5px solid #008cd0',
              bgcolor: '#e7fbff',
              color: '#3b82f6',
              fontSize: '10px',
              fontWeight: 500,
              lineHeight: '16px',
            }}
          >
            <Typography
              component="span"
              sx={{
                fontSize: 'inherit',
                fontWeight: 'inherit',
                lineHeight: 'inherit',
                color: 'inherit',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {card.packageLabel}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Menu
        anchorEl={menuAnchor}
        open={menuOpen}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: sensemakerCardMenuPaperSx,
          },
        }}
      >
        {showDeactivate ? (
          <MenuItem
            data-testid={`sensemaker-card-deactivate-${card.tenantId}`}
            onClick={() => {
              setMenuAnchor(null)
              onDeactivate(card)
            }}
            sx={sensemakerCardDeactivateMenuItemSx}
          >
            Deactivate Tenant
          </MenuItem>
        ) : null}
        {showReactivate ? (
          <MenuItem
            data-testid={`sensemaker-card-reactivate-${card.tenantId}`}
            onClick={() => {
              setMenuAnchor(null)
              onReactivate(card)
            }}
            sx={sensemakerCardReactivateMenuItemSx}
          >
            Reactivate Tenant
          </MenuItem>
        ) : null}
      </Menu>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', mb: '13px' }}>
        {STAT_TILES.map((tile) => {
          const rawValue = card[tile.field]
          const value = tile.field === 'teamMemberCount' ? (rawValue ?? '—') : rawValue
          return (
            <Box
              key={tile.key}
              sx={{
                border: `1px solid ${tile.border}`,
                bgcolor: tile.bg,
                borderRadius: '8px',
                height: '52px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'flex-start',
                pl: tile.pl,
                pr: '16px',
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: '18px',
                  lineHeight: '32px',
                  color: '#0b1c30',
                }}
              >
                {value}
              </Typography>
              <Typography
                sx={{
                  fontSize: '9px',
                  fontWeight: 500,
                  lineHeight: tile.key === 'team' ? '16px' : '14px',
                  letterSpacing: '0.12px',
                  color: '#3f4850',
                }}
              >
                {tile.label}
              </Typography>
            </Box>
          )
        })}
      </Box>

      <Box
        data-testid={`sensemaker-card-info-${card.tenantId}`}
        sx={{
          bgcolor: '#eef9ff',
          border: '1px solid #e1e7f3',
          borderRadius: '8px',
          height: `${SENSEMAKER_LIST_CARD_INFO_HEIGHT_PX}px`,
          display: 'flex',
          alignItems: 'center',
          pl: '11px',
          pr: '11px',
          mb: `${SENSEMAKER_LIST_CARD_INFO_TO_TOKEN_GAP_PX}px`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: `${SENSEMAKER_LIST_CARD_INFO_METRIC_GAP_PX}px`,
          }}
        >
          <SensemakerCardInfoMetric
            icon={<SensemakerAmountIcon sx={{ fontSize: 12, color: '#0d9467' }} />}
            value={formatUsd(card.amountDistributedUsd)}
            valueColor="#0d9467"
            label="Amount Distributed"
          />
          <SensemakerCardInfoMetric
            icon={<SensemakerOnboardedIcon sx={{ fontSize: 12, color: '#3b82f6' }} />}
            value={formatOnboardedDate(card.onboardedAt)}
            valueColor="#3b82f6"
            label="Onboarded"
          />
        </Box>
      </Box>

      <Box sx={{ mt: 'auto', width: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: `${SENSEMAKER_LIST_CARD_TOKEN_LABEL_TO_PROGRESS_GAP_PX}px`,
          }}
        >
          <Typography
            sx={{
              fontSize: '12px',
              lineHeight: '16px',
              fontWeight: 400,
              color: '#000000',
            }}
          >
            Campaign Token Balance
          </Typography>
          <Typography
            sx={{
              fontSize: '12px',
              lineHeight: '16px',
              fontWeight: 500,
              color: '#000000',
            }}
          >
            {card.campaignTokenBalanceLabel}
          </Typography>
        </Box>
        <Box
          sx={{
            height: '4px',
            borderRadius: '9999px',
            bgcolor: '#e2e8f0',
          }}
        />
        <Button
          variant="contained"
          fullWidth
          data-testid={`sensemaker-card-view-more-${card.tenantId}`}
          onClick={() => navigate(`/tenants/${card.tenantId}`)}
          sx={{
            mt: `${SENSEMAKER_LIST_CARD_PROGRESS_TO_BUTTON_GAP_PX}px`,
            height: `${SENSEMAKER_LIST_CARD_BUTTON_HEIGHT_PX}px`,
            textTransform: 'none',
            borderRadius: '8px',
            bgcolor: '#008cd0',
            fontSize: '12px',
            fontWeight: 600,
            lineHeight: '16px',
            boxShadow: 'none',
            '&:hover': { bgcolor: '#0076b0', boxShadow: 'none' },
          }}
        >
          View More
        </Button>
      </Box>
    </Box>
  )
}
